import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Sparkles, AlertCircle, Check, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface SuggestedSkill {
  name: string;
  type: string;
  parentBucket?: string;
  confidence?: number;
  reason?: string;
}

interface AISkillSuggestionsProps {
  existingSkills: string[];
  onAddSkills: (skills: { name: string; type: string }[]) => void;
}

const AISkillSuggestions = ({ existingSkills, onAddSkills }: AISkillSuggestionsProps) => {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestedSkill[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchSuggestions = async () => {
    if (!domain.trim()) {
      toast({ title: 'Please enter a domain', variant: 'destructive' });
      return;
    }

    setLoading(true);
    setError(null);
    setSuggestions([]);
    setSelectedSkills(new Set());

    try {
      const { data, error: fnError } = await supabase.functions.invoke('suggest-skills', {
        body: { domain, existingSkills, action: 'suggest_for_domain' }
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      setSuggestions(data?.skills || []);
    } catch (err: any) {
      console.error('Error fetching suggestions:', err);
      setError(err.message || 'Failed to get suggestions');
    } finally {
      setLoading(false);
    }
  };

  const toggleSkill = (skillName: string) => {
    const newSelected = new Set(selectedSkills);
    if (newSelected.has(skillName)) {
      newSelected.delete(skillName);
    } else {
      newSelected.add(skillName);
    }
    setSelectedSkills(newSelected);
  };

  const handleAddSelected = () => {
    const skillsToAdd = suggestions
      .filter(s => selectedSkills.has(s.name))
      .map(s => ({ name: s.name, type: s.type }));
    
    onAddSkills(skillsToAdd);
    setSelectedSkills(new Set());
    setSuggestions(prev => prev.filter(s => !selectedSkills.has(s.name)));
    toast({ title: `Added ${skillsToAdd.length} skills` });
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'technical': return 'bg-blue-500/10 text-blue-700 dark:text-blue-400';
      case 'framework': return 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-400';
      case 'tool': return 'bg-purple-500/10 text-purple-700 dark:text-purple-400';
      case 'soft': return 'bg-pink-500/10 text-pink-700 dark:text-pink-400';
      default: return 'bg-gray-500/10 text-gray-700 dark:text-gray-400';
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-5 w-5 text-amber-500" />
        <h3 className="font-semibold">AI Skill Suggestions</h3>
      </div>

      <div className="flex gap-2 mb-4">
        <Input
          placeholder="Enter a domain (e.g., Salesforce, Machine Learning)"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && fetchSuggestions()}
        />
        <Button onClick={fetchSuggestions} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Suggest'}
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-destructive text-sm mb-4">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {suggestions.length > 0 && (
        <>
          <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
            {suggestions.map((skill, idx) => {
              const isExisting = existingSkills.some(
                e => e.toLowerCase() === skill.name.toLowerCase()
              );
              return (
                <div
                  key={idx}
                  className={`flex items-center gap-3 p-2 rounded-md border ${
                    isExisting ? 'opacity-50 bg-muted' : 'hover:bg-accent/50'
                  }`}
                >
                  <Checkbox
                    checked={selectedSkills.has(skill.name)}
                    onCheckedChange={() => !isExisting && toggleSkill(skill.name)}
                    disabled={isExisting}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{skill.name}</span>
                      {isExisting && (
                        <Badge variant="outline" className="text-xs">
                          <Check className="h-3 w-3 mr-1" />
                          Exists
                        </Badge>
                      )}
                    </div>
                    {skill.reason && (
                      <p className="text-xs text-muted-foreground">{skill.reason}</p>
                    )}
                  </div>
                  <Badge className={getTypeColor(skill.type)}>{skill.type}</Badge>
                </div>
              );
            })}
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              {selectedSkills.size} selected
            </span>
            <Button onClick={handleAddSelected} disabled={selectedSkills.size === 0}>
              <Plus className="h-4 w-4 mr-2" />
              Add Selected Skills
            </Button>
          </div>
        </>
      )}

      {!loading && suggestions.length === 0 && !error && (
        <p className="text-sm text-muted-foreground text-center py-4">
          Enter a domain above to get AI-powered skill suggestions
        </p>
      )}
    </Card>
  );
};

export default AISkillSuggestions;